import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LocationMarkerIcon, 
  CurrencyDollarIcon, 
  EyeIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  DesktopComputerIcon,
  ClockIcon,
  UserGroupIcon,
  XIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowSmRightIcon,
  PlusCircleIcon,
  HeartIcon,
  StarIcon
} from '@heroicons/react/outline';

// Données de démonstration pour les sites
const siteData = [
  {
    id: 1,
    name: "Centre Commercial Douala Grand Mall",
    type: "Centre Commercial",
    location: "Douala, Akwa",
    screens: 4,
    screenType: "LCD HD 55\"",
    pricePerDay: 45000,
    views: 15000,
    availability: true,
    trafficType: "Élevé",
    peakHours: "14h - 20h",
    avgAge: "18-45 ans",
    demographics: "Familles, Jeunes actifs",
    description: "Situé au cœur de Douala, le Grand Mall attire quotidiennement une clientèle diverse et aisée. Nos 4 écrans stratégiquement placés garantissent une visibilité maximale.",
    imageUrl: "/assets/site_carrefour1.jpg",
    lastCampaigns: ["Coca-Cola", "MTN", "Orange", "Canal+"],
    screenLocations: ["Entrée principale", "Food court", "Escalators", "Parking"],
    rating: 4.8,
    reviews: 124
  },
  {
    id: 2,
    name: "Hôtel Akwa Palace",
    type: "Hôtel",
    location: "Douala, Bonanjo",
    screens: 2,
    screenType: "Totem Numérique Interactif",
    pricePerDay: 35000,
    views: 5000,
    availability: true,
    trafficType: "Moyen - Haut de gamme",
    peakHours: "8h - 10h, 18h - 22h",
    avgAge: "30-55 ans",
    demographics: "Hommes d'affaires, Touristes internationaux",
    description: "Hôtel 5 étoiles accueillant une clientèle professionnelle et internationale. Écrans situés dans le lobby et près des salles de conférence offrant une visibilité ciblée.",
    imageUrl: "/assets/site_carrefour2.jpg",
    lastCampaigns: ["Martell", "Emirates", "BMW", "BICEC"],
    screenLocations: ["Hall d'entrée", "Espace conférence"],
    rating: 4.6,
    reviews: 86
  },
  {
    id: 3,
    name: "Restaurant La Fourchette",
    type: "Restaurant",
    location: "Douala, Bonapriso",
    screens: 1,
    screenType: "Écran LED Mural",
    pricePerDay: 20000,
    views: 800,
    availability: false,
    trafficType: "Moyen - Premium",
    peakHours: "12h - 14h, 19h - 23h",
    avgAge: "25-50 ans",
    demographics: "Cadres, Expatriés, Couples",
    description: "Restaurant haut de gamme proposant une cuisine internationale. Clientèle fidèle à fort pouvoir d'achat. Écran idéal pour les produits premium et de luxe.",
    imageUrl: "/assets/site_carrefour3.jpg",
    lastCampaigns: ["Heineken", "Glenfiddich", "Nespresso", "UBA Premium"],
    screenLocations: ["Salle principale"],
    rating: 4.4,
    reviews: 58
  },
  {
    id: 4,
    name: "Agence de Voyage Sky Tours",
    type: "Agence de Voyage",
    location: "Douala, Bali",
    screens: 3,
    screenType: "LCD HD 42\"",
    pricePerDay: 30000,
    views: 1200,
    availability: true,
    trafficType: "Ciblé",
    peakHours: "9h - 17h",
    avgAge: "30-60 ans",
    demographics: "Voyageurs d'affaires, Touristes, Familles",
    description: "Agence spécialisée dans les voyages premium et d'affaires. Clientèle à la recherche de services haut de gamme et disposant d'un budget conséquent.",
    imageUrl: "/assets/site_3.jpg",
    lastCampaigns: ["Air France", "Radisson Blu", "Kenya Airways", "Brussels Airlines"],
    screenLocations: ["Zone d'attente", "Bureau des conseillers", "Vitrine extérieure"],
    rating: 4.2,
    reviews: 42
  }
];

const NetworkDisplay = () => {
  const [selectedSite, setSelectedSite] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  const SITES_PER_PAGE = 2;
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Filtre les sites selon les critères
  const filteredSites = siteData.filter(site => {
    // Filtrer par disponibilité
    if (filter === 'available' && !site.availability) return false;
    if (filter === 'reserved' && site.availability) return false;
    
    // Filtrer par terme de recherche
    if (searchTerm && !site.name.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !site.location.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !site.type.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    return true;
  });
  
  // Calculate current sites to display based on pagination
  const currentSites = filteredSites.slice(
    currentPage * SITES_PER_PAGE, 
    (currentPage + 1) * SITES_PER_PAGE
  );

  // Reset pagination when filter changes
  useEffect(() => {
    setCurrentPage(0);
  }, [filter, searchTerm]);

  const openModal = (site) => {
    setSelectedSite(site);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const pageCount = Math.ceil(filteredSites.length / SITES_PER_PAGE);

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-3 sm:px-4 py-8 sm:py-12 lg:py-16 max-w-6xl">
        {/* Header compact */}
        <motion.div 
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-block relative mb-2">
            <motion.div 
              animate={{ 
                scale: [1, 1.15, 1],
                rotate: [0, 3, 0, -3, 0]
              }}
              transition={{ 
                duration: 5,
                repeat: Infinity,
                repeatType: "reverse"
              }}
              className="absolute -top-4 -left-4 w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#FFF4F0] -z-10"
            ></motion.div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-800 relative inline-block">
              Notre Réseau d'Affichage
            </h1>
            <motion.div 
              animate={{ 
                scale: [1, 1.08, 1],
                rotate: [0, -2, 0, 2, 0]
              }}
              transition={{ 
                duration: 6,
                repeat: Infinity,
                repeatType: "reverse",
                delay: 0.3
              }}
              className="absolute -bottom-1 -right-6 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#FFE8DD] -z-10"
            ></motion.div>
          </div>
          <div className="w-16 sm:w-20 h-1 bg-[#E84E0E] mx-auto my-3 rounded-full"></div>
          <p className="mt-3 text-gray-600 max-w-xl mx-auto text-sm sm:text-base lg:text-lg px-4">
            Découvrez notre réseau d'écrans publicitaires stratégiquement placés dans les zones 
            à fort trafic pour maximiser la visibilité de vos campagnes.
          </p>
        </motion.div>
        
        {/* Barre de recherche et filtres compacte */}
        <motion.div 
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="mb-8 sm:mb-10 bg-white rounded-xl shadow-md p-4 sm:p-5"
        >
          <div className="flex flex-col gap-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                className="block w-full pl-9 sm:pl-10 pr-3 py-3 border border-gray-200 rounded-lg text-sm sm:text-base text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#E84E0E] focus:border-transparent"
                placeholder="Rechercher par nom, lieu, type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex gap-2 overflow-x-auto pb-1">
              <FilterButton 
                active={filter === 'all'} 
                onClick={() => setFilter('all')}
                label="Tous"
              />
              <FilterButton 
                active={filter === 'available'} 
                onClick={() => setFilter('available')}
                label="Disponibles"
                icon={<CheckCircleIcon className="h-4 w-4 mr-1.5" />}
              />
              <FilterButton 
                active={filter === 'reserved'} 
                onClick={() => setFilter('reserved')}
                label="Réservés"
                icon={<XCircleIcon className="h-4 w-4 mr-1.5" />}
              />
            </div>
          </div>
        </motion.div>
        
        {/* Message si aucun résultat */}
        {currentSites.length === 0 && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <SearchIcon className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">Aucun site ne correspond à votre recherche</h3>
            <p className="text-gray-500 text-sm">Essayez de modifier vos filtres ou votre terme de recherche</p>
            <button 
              onClick={() => {setSearchTerm(''); setFilter('all');}}
              className="mt-4 px-5 py-2.5 bg-[#E84E0E] text-white rounded-lg hover:bg-[#d24609] transition-colors text-sm"
            >
              Réinitialiser les filtres
            </button>
          </motion.div>
        )}
        
        {/* Grille des sites - Layout adaptatif */}
        {currentSites.length > 0 && (
          <div className="relative mb-8 sm:mb-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
              {currentSites.map((site, index) => (
                <SiteCard 
                  key={site.id} 
                  site={site} 
                  onClick={() => openModal(site)} 
                  index={index}
                  windowSize={windowSize}
                />
              ))}
            </div>
            
            {/* Contrôles de navigation compacts */}
            {pageCount > 1 && (
              <div className="flex justify-between absolute -left-3 -right-3 top-1/2 -translate-y-1/2 px-2 z-10">
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 0))}
                  disabled={currentPage === 0}
                  className={`w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center transition-all duration-300 ${
                    currentPage === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#FFF4F0]'
                  }`}
                >
                  <ChevronLeftIcon className="h-5 w-5 text-gray-700" />
                </motion.button>
                <motion.button 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, pageCount - 1))}
                  disabled={currentPage >= pageCount - 1}
                  className={`w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center transition-all duration-300 ${
                    currentPage >= pageCount - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#FFF4F0]'
                  }`}
                >
                  <ChevronRightIcon className="h-5 w-5 text-gray-700" />
                </motion.button>
              </div>
            )}
          </div>
        )}
        
        {/* Pagination compacte */}
        {pageCount > 1 && (
          <div className="flex justify-center gap-1.5 my-6">
            {Array.from({ length: pageCount }).map((_, i) => (
              <motion.button 
                key={i}
                onClick={() => setCurrentPage(i)}
                whileHover={{ scale: 1.15 }}
                whileTap={{ scale: 0.9 }}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  i === currentPage ? 'bg-[#E84E0E] w-8' : 'bg-gray-300 hover:bg-gray-400 w-2.5'
                }`}
                aria-label={`Page ${i + 1}`}
              />
            ))}
          </div>
        )}

        {/* Modal détaillé adaptatif */}
        <AnimatePresence>
          {isModalOpen && selectedSite && (
            <SiteDetailModal 
              site={selectedSite} 
              onClose={closeModal} 
              windowSize={windowSize}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// Composant pour les boutons de filtre compacts
const FilterButton = ({ active, onClick, label, icon }) => {
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`px-3 sm:px-4 py-2.5 rounded-lg flex items-center transition-all duration-300 text-sm font-medium whitespace-nowrap ${
        active 
          ? 'bg-[#E84E0E] text-white shadow-sm' 
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {icon}
      <span>{label}</span>
    </motion.button>
  );
};

// Icône de recherche pour le message "aucun résultat"
const SearchIcon = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

// Carte de site optimisée pour petits écrans
const SiteCard = ({ site, onClick, index, windowSize }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  // Hauteur adaptative selon la taille d'écran
  const getCardHeight = () => {
    if (windowSize.height <= 768) return 'h-[280px]'; // Très petits écrans
    if (windowSize.height <= 900) return 'h-[300px]'; // 14" typique
    return 'h-[320px]'; // Écrans plus grands
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="bg-white rounded-2xl overflow-hidden shadow-md cursor-pointer transition-all duration-300 relative group hover:shadow-lg"
      onClick={onClick}
    >
      <motion.div 
        animate={isHovered ? { scale: 1.02 } : { scale: 1 }}
        transition={{ duration: 0.3 }}
        className={`bg-center bg-cover relative ${getCardHeight()}`}
        style={{ backgroundImage: `url(${site.imageUrl})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/75 group-hover:from-black/20 group-hover:via-black/30 group-hover:to-black/85 transition-all duration-300"></div>
        
        {/* Badges compacts */}
        <div className="absolute top-3 left-3 z-10">
          <div className="bg-white px-3 py-1.5 rounded-full shadow-sm">
            <span className="font-medium text-xs text-gray-800">{site.type}</span>
          </div>
        </div>
        
        <div className="absolute top-3 right-3 z-10">
          {site.availability ? (
            <div className="bg-green-500 px-3 py-1.5 rounded-full shadow-sm flex items-center">
              <CheckCircleIcon className="h-3 w-3 mr-1 text-white" />
              <span className="font-medium text-xs text-white">Disponible</span>
            </div>
          ) : (
            <div className="bg-red-500 px-3 py-1.5 rounded-full shadow-sm flex items-center">
              <XCircleIcon className="h-3 w-3 mr-1 text-white" />
              <span className="font-medium text-xs text-white">Réservé</span>
            </div>
          )}
        </div>
        
        {/* Notation compacte */}
        <div className="absolute top-12 right-3 z-10">
          <div className="bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full shadow-sm flex items-center">
            <StarIcon className="h-3 w-3 mr-1 text-yellow-500" />
            <span className="font-medium text-xs text-gray-800">{site.rating}</span>
            <span className="text-xs text-gray-500 ml-0.5">({site.reviews})</span>
          </div>
        </div>
        
        {/* Information du lieu compacte */}
        <div className="absolute bottom-3 left-3 right-3 z-10">
          <h3 className="text-lg sm:text-xl font-bold mb-1 text-white drop-shadow-sm truncate">{site.name}</h3>
          <div className="flex items-center text-white/90 mb-2">
            <LocationMarkerIcon className="h-3 w-3 mr-1" />
            <span className="text-xs sm:text-sm truncate">{site.location}</span>
          </div>
        </div>
        
        {/* Bouton voir détails compact */}
        <motion.div 
          className="absolute inset-0 flex items-center justify-center z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          initial={{ opacity: 0 }}
          animate={isHovered ? { opacity: 1 } : { opacity: 0 }}
        >
          <motion.button 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-5 py-2.5 bg-white rounded-lg font-medium text-[#E84E0E] shadow-lg flex items-center space-x-2 text-sm"
          >
            <span>Voir les détails</span>
            <ArrowSmRightIcon className="h-4 w-4" />
          </motion.button>
        </motion.div>
      </motion.div>
      
      {/* Contenu sous l'image compact */}
      <div className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="p-2 bg-[#FFF4F0] rounded-lg mr-3">
              <DesktopComputerIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#E84E0E]" />
            </div>
            <div>
              <span className="block text-xs text-gray-500">Écrans</span>
              <span className="text-lg font-semibold text-gray-800">{site.screens}</span>
            </div>
          </div>
          
          <div className="flex items-center">
            <div className="p-2 bg-[#FFF4F0] rounded-lg mr-3">
              <EyeIcon className="h-4 w-4 sm:h-5 sm:w-5 text-[#E84E0E]" />
            </div>
            <div>
              <span className="block text-xs text-gray-500">Vues/jour</span>
              <span className="text-lg font-semibold text-gray-800">{new Intl.NumberFormat('fr-FR').format(site.views)}</span>
            </div>
          </div>
        </div>
        
        <div className="flex justify-between items-center py-3 border-t border-gray-100">
          <div>
            <span className="block text-xs text-gray-500">Prix par jour</span>
            <span className="text-lg sm:text-xl font-bold text-[#E84E0E]">{new Intl.NumberFormat('fr-FR').format(site.pricePerDay)} FCFA</span>
          </div>
          
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-10 h-10 rounded-full bg-[#FFF4F0] hover:bg-[#FFE0D0] flex items-center justify-center transition-colors"
          >
            <HeartIcon className="h-5 w-5 text-[#E84E0E]" />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

// Modal détaillé optimisé pour petits écrans
const SiteDetailModal = ({ site, onClose, windowSize }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [imageLoaded, setImageLoaded] = useState(false);
  
  useEffect(() => {
    const img = new Image();
    img.src = site.imageUrl;
    img.onload = () => setImageLoaded(true);
  }, [site.imageUrl]);
  
  // Dimensions adaptatives du modal
  const getModalDimensions = () => {
    if (windowSize.height <= 768) {
      return {
        maxHeight: 'max-h-[95vh]',
        headerHeight: 'h-48',
        padding: 'p-3'
      };
    }
    if (windowSize.height <= 900) {
      return {
        maxHeight: 'max-h-[90vh]',
        headerHeight: 'h-56',
        padding: 'p-4'
      };
    }
    return {
      maxHeight: 'max-h-[85vh]',
      headerHeight: 'h-64',
      padding: 'p-6'
    };
  };

  const modalDimensions = getModalDimensions();
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black bg-opacity-75 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 15 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className={`bg-white rounded-2xl shadow-2xl overflow-hidden max-w-4xl w-full ${modalDimensions.maxHeight} flex flex-col`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header compact avec image */}
        <div className="relative">
          <motion.div 
            initial={{ scale: 1.02 }}
            animate={{ scale: imageLoaded ? 1 : 1.02 }}
            transition={{ duration: 0.4 }}
            className={`bg-center bg-cover relative ${modalDimensions.headerHeight}`}
            style={{ backgroundImage: `url(${site.imageUrl})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20"></div>
            
            {/* Bouton de fermeture */}
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="absolute top-3 right-3 p-2 rounded-full bg-white/20 hover:bg-white/40 text-white transition-all z-10"
              onClick={onClose}
            >
              <XIcon className="h-5 w-5" />
            </motion.button>
            
            {/* Badges compacts */}
            <motion.div 
              initial={{ x: -15, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="absolute left-3 top-3 bg-white px-4 py-1.5 rounded-full shadow-sm"
            >
              <span className="font-medium text-sm text-gray-800">{site.type}</span>
            </motion.div>
            
            <motion.div 
              initial={{ x: 15, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="absolute right-3 top-12 z-10"
            >
              {site.availability ? (
                <div className="bg-green-500 px-4 py-1.5 rounded-full shadow-sm flex items-center">
                  <CheckCircleIcon className="h-4 w-4 mr-1.5 text-white" />
                  <span className="font-medium text-sm text-white">Disponible</span>
                </div>
              ) : (
                <div className="bg-red-500 px-4 py-1.5 rounded-full shadow-sm flex items-center">
                  <XCircleIcon className="h-4 w-4 mr-1.5 text-white" />
                  <span className="font-medium text-sm text-white">Réservé</span>
                </div>
              )}
            </motion.div>
            
            {/* Notation */}
            <motion.div 
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="absolute right-3 top-20 z-10"
            >
              <div className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-sm flex items-center">
                <StarIcon className="h-4 w-4 mr-1 text-yellow-500" />
                <span className="font-medium text-sm text-gray-800">{site.rating}</span>
                <span className="text-xs text-gray-500 ml-1">({site.reviews} avis)</span>
              </div>
            </motion.div>
            
            {/* Nom et emplacement */}
            <motion.div 
              initial={{ y: 15, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="absolute bottom-3 left-3 right-3"
            >
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">{site.name}</h2>
              <div className="flex items-center text-white/90 mt-2">
                <LocationMarkerIcon className="h-4 w-4 mr-1.5" />
                <span className="text-sm sm:text-base">{site.location}</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
        
        {/* Navigation par onglets compacte */}
        <div className="border-b border-gray-200 overflow-x-auto">
          <div className="flex px-4 py-1 min-w-max">
            <TabButton 
              active={activeTab === 'info'} 
              onClick={() => setActiveTab('info')}
              label="Infos"
            />
            <TabButton 
              active={activeTab === 'screens'} 
              onClick={() => setActiveTab('screens')}
              label="Écrans"
            />
            <TabButton 
              active={activeTab === 'audience'} 
              onClick={() => setActiveTab('audience')}
              label="Audience"
            />
            <TabButton 
              active={activeTab === 'campaigns'} 
              onClick={() => setActiveTab('campaigns')}
              label="Campagnes"
            />
          </div>
        </div>
        
        {/* Contenu des onglets - scrollable */}
        <div className={`flex-grow overflow-y-auto ${modalDimensions.padding}`}>
          <AnimatePresence mode="wait">
            {activeTab === 'info' && (
              <motion.div
                key="info"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">Description</h3>
                    <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{site.description}</p>
                    
                    <div className="mt-6">
                      <h3 className="text-lg font-bold text-gray-800 mb-3">Prix</h3>
                      <div className="flex items-baseline space-x-2">
                        <span className="text-2xl sm:text-3xl font-bold text-[#E84E0E]">
                          {new Intl.NumberFormat('fr-FR').format(site.pricePerDay)}
                        </span>
                        <span className="text-gray-600">FCFA / jour</span>
                      </div>
                      <p className="text-gray-500 text-sm mt-2">
                        Prix dégressifs pour les campagnes longue durée
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-3">Détails du site</h3>
                    <ul className="space-y-3">
                      <InfoItem 
                        icon={<DesktopComputerIcon className="h-4 w-4 text-[#E84E0E]" />}
                        label="Type d'écran"
                        value={site.screenType}
                      />
                      <InfoItem 
                        icon={<LocationMarkerIcon className="h-4 w-4 text-[#E84E0E]" />}
                        label="Emplacement"
                        value={site.location}
                      />
                      <InfoItem 
                        icon={<CurrencyDollarIcon className="h-4 w-4 text-[#E84E0E]" />}
                        label="Prix par jour"
                        value={`${new Intl.NumberFormat('fr-FR').format(site.pricePerDay)} FCFA`}
                      />
                      <InfoItem 
                        icon={<EyeIcon className="h-4 w-4 text-[#E84E0E]" />}
                        label="Vues quotidiennes"
                        value={new Intl.NumberFormat('fr-FR').format(site.views)}
                      />
                      <InfoItem 
                        icon={<ClockIcon className="h-4 w-4 text-[#E84E0E]" />}
                        label="Heures d'affluence"
                        value={site.peakHours}
                      />
                    </ul>
                  </div>
                </div>
                
                <div className="mt-8">
                  <motion.button 
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`px-6 py-3 rounded-lg font-medium text-white shadow-md flex items-center ${
                      site.availability ? 'bg-[#E84E0E] hover:bg-[#d24609]' : 'bg-gray-400 cursor-not-allowed'
                    }`}
                    disabled={!site.availability}
                  >
                    <PlusCircleIcon className="h-4 w-4 mr-2" />
                    <span>Réserver cet emplacement</span>
                  </motion.button>
                </div>
              </motion.div>
            )}
            
            {activeTab === 'screens' && (
              <motion.div
                key="screens"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="text-lg font-bold text-gray-800 mb-4">Emplacements des écrans</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {site.screenLocations.map((location, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-gray-50 rounded-lg p-4 flex items-center"
                    >
                      <div className="w-10 h-10 bg-[#FFF4F0] rounded-full flex items-center justify-center mr-3">
                        <span className="text-[#E84E0E] font-bold text-sm">{index + 1}</span>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-800 text-sm">{location}</h4>
                        <p className="text-gray-500 text-xs">Écran {site.screenType}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                <div className="mt-6 bg-[#FFF4F0] rounded-lg p-4">
                  <h3 className="text-base font-bold text-gray-800 mb-2">Spécifications techniques</h3>
                  <p className="text-gray-600 text-sm">
                    Tous les écrans de ce site sont des {site.screenType} avec une résolution HD et un système de diffusion automatisé.
                    Les formats acceptés sont JPG, PNG et MP4 (vidéos de 15 à 30 secondes max).
                  </p>
                </div>
              </motion.div>
            )}
            
            {activeTab === 'audience' && (
              <motion.div
                key="audience"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Profil de l'audience</h3>
                    <div className="space-y-4">
                      <InfoItem 
                        icon={<UserGroupIcon className="h-4 w-4 text-[#E84E0E]" />}
                        label="Type de trafic"
                        value={site.trafficType}
                      />
                      <InfoItem 
                        icon={<ClockIcon className="h-4 w-4 text-[#E84E0E]" />}
                        label="Heures d'affluence"
                        value={site.peakHours}
                      />
                      <InfoItem 
                        icon={<UserGroupIcon className="h-4 w-4 text-[#E84E0E]" />}
                        label="Âge moyen"
                        value={site.avgAge}
                      />
                      <InfoItem 
                        icon={<UserGroupIcon className="h-4 w-4 text-[#E84E0E]" />}
                        label="Profil démographique"
                        value={site.demographics}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Impact estimé</h3>
                    <div className="bg-white rounded-lg shadow-sm p-4">
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between mb-2">
                            <span className="text-gray-600 text-sm">Vues quotidiennes</span>
                            <span className="font-bold text-gray-800 text-sm">{new Intl.NumberFormat('fr-FR').format(site.views)}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <motion.div 
                              initial={{ width: 0 }}
                              animate={{ width: '85%' }}
                              transition={{ duration: 0.8, delay: 0.1 }}
                              className="bg-[#E84E0E] h-2 rounded-full"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex justify-between mb-2">
                            <span className="text-gray-600 text-sm">Engagement moyen</span>
                            <span className="font-bold text-gray-800 text-sm">65%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <motion.div 
                              initial={{ width: 0 }}
                              animate={{ width: '65%' }}
                              transition={{ duration: 0.8, delay: 0.3 }}
                              className="bg-[#E84E0E] h-2 rounded-full"
                            />
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex justify-between mb-2">
                            <span className="text-gray-600 text-sm">Mémorisation</span>
                            <span className="font-bold text-gray-800 text-sm">72%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <motion.div 
                              initial={{ width: 0 }}
                              animate={{ width: '72%' }}
                              transition={{ duration: 0.8, delay: 0.5 }}
                              className="bg-[#E84E0E] h-2 rounded-full"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
            
            {activeTab === 'campaigns' && (
              <motion.div
                key="campaigns"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                <h3 className="text-lg font-bold text-gray-800 mb-4">Campagnes récentes</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {site.lastCampaigns.map((campaign, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="bg-gray-50 rounded-lg p-3 text-center"
                    >
                      <div className="w-12 h-12 mx-auto bg-white rounded-full flex items-center justify-center mb-2 shadow-sm">
                        <span className="text-lg font-bold text-[#E84E0E]">{campaign.charAt(0)}</span>
                      </div>
                      <h4 className="font-medium text-gray-800 text-sm">{campaign}</h4>
                    </motion.div>
                  ))}
                </div>
                
                <div className="mt-6 bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="p-2 bg-[#FFF4F0] rounded-full mr-3">
                      <EyeIcon className="h-5 w-5 text-[#E84E0E]" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-800 mb-1 text-sm">Efficacité prouvée</h4>
                      <p className="text-gray-600 text-sm">
                        Les campagnes sur ce site ont démontré un taux d'engagement de 25% supérieur
                        à la moyenne nationale pour des emplacements similaires.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Footer compact avec bouton d'action */}
        <div className="border-t border-gray-200 p-3 sm:p-4 flex flex-col sm:flex-row justify-between items-center bg-gray-50 gap-3 sm:gap-0">
          <div className="text-center sm:text-left">
            <span className="block text-xs text-gray-500">Prix par jour</span>
            <span className="text-xl sm:text-2xl font-bold text-[#E84E0E]">
              {new Intl.NumberFormat('fr-FR').format(site.pricePerDay)} FCFA
            </span>
          </div>
          
          <motion.button 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`px-6 py-3 rounded-lg font-medium text-white shadow-md flex items-center text-sm ${
              site.availability ? 'bg-[#E84E0E] hover:bg-[#d24609]' : 'bg-gray-400 cursor-not-allowed'
            }`}
            disabled={!site.availability}
          >
            <PlusCircleIcon className="h-4 w-4 mr-2" />
            <span>Réserver maintenant</span>
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Composant TabButton compact pour les onglets
const TabButton = ({ active, onClick, label }) => {
  return (
    <button
      onClick={onClick}
      className={`py-3 px-4 font-medium text-sm relative ${
        active ? 'text-[#E84E0E]' : 'text-gray-500 hover:text-gray-700'
      }`}
    >
      {label}
      {active && (
        <motion.div 
          layoutId="activeTab"
          className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#E84E0E] rounded-t-full"
        />
      )}
    </button>
  );
};

// Composant InfoItem compact pour afficher les informations
const InfoItem = ({ icon, label, value }) => {
  return (
    <div className="flex items-start">
      <div className="p-1.5 bg-[#FFF4F0] rounded-lg mr-2.5">
        {icon}
      </div>
      <div>
        <span className="block text-xs text-gray-500">{label}</span>
        <span className="font-medium text-gray-800 text-sm">{value}</span>
      </div>
    </div>
  );
};

export default NetworkDisplay;